import type { PrimitiveColors } from '../colors/colors';
import { colors } from '../colors/colors';

import type { ThemeColorValue, ThemeColors, ParsedThemeColors } from './types';

/**
 * Type for parsed color output - can be a string, number, or nested object
 */
export type ParsedColorValue = string | number | Record<string, unknown>;
export type ParsedColors = ParsedThemeColors;

/**
 * Parses color references in a theme colors object and replaces them with actual hex values
 *
 * @param themeColors - Theme colors object that may contain color references like 'mintTulip.500' or plain color names like 'black', 'white'
 * @returns Parsed colors object with hex values
 */
const parseColors = (themeColors: ThemeColors): ParsedColors => {
  const parseValue = (value: ThemeColorValue): ParsedColorValue => {
    // If value is a string
    if (typeof value === 'string') {
      // Check if it's a dot-notation color reference (e.g., 'mintTulip.500')
      if (value.includes('.')) {
        const [colorName, shade] = value.split('.');

        // Check if this is a valid color reference
        if (colors[colorName as keyof PrimitiveColors] && shade) {
          const colorScale = colors[colorName as keyof PrimitiveColors];
          // Check if the shade is a valid key in the color scale (only for object scales, not primitive strings)
          if (typeof colorScale === 'object' && colorScale !== null && shade in colorScale) {
            const hexValue = (colorScale as Record<string, string>)[shade];
            if (hexValue) {
              return hexValue;
            }
          }
        }
      } else {
        // Check if it's a plain color name reference (e.g., 'black', 'white')
        if (colors[value as keyof PrimitiveColors]) {
          const colorValue = colors[value as keyof PrimitiveColors];
          // If it's a string (primitive color like black/white), return it directly
          if (typeof colorValue === 'string') {
            return colorValue;
          }
        }
      }
    }

    // If value is an object, recursively parse its properties
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const parsed: Record<string, ParsedColorValue> = {};
      for (const [key, val] of Object.entries(value)) {
        parsed[key] = parseValue(val as ThemeColorValue);
      }
      return parsed;
    }

    // Return the value as-is if it's not a color reference or object
    return value;
  };

  const parsedColors: ParsedColors = {};

  for (const [key, value] of Object.entries(themeColors)) {
    parsedColors[key] = parseValue(value);
  }

  return parsedColors;
};

export default parseColors;
